import React, { useEffect, useMemo, useRef, useState } from 'react';
import { accountsReducer, selectedAccStateSelector } from '@core/store/library-stores';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { Button, styleConstants, logout, selectOidcUserLogged, selectUserProfileId } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { makeStyles, Menu, Theme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import PersonIcon from '@material-ui/icons/Person';
import { environment } from '@root/environment';
import { useUserDetailsDataSource } from './userDetailsDataSource';

export const useStyles = makeStyles((theme: Theme) => ({
  userDetailsMenuWrapper: {
    top: '66px !important',
    left: 'unset !important',
    right: 0,
  },
  userDetailsMenuListWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
    padding: '24px',
    color: styleConstants.colors.grey,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '16px',
    '& > *:not(:last-child)': {
      marginBottom: '8px',
    },
  },
  version: {
    fontSize: '12px',
    textAlign: 'right',
    width: '100%',
    color: '#B6BCCE',
    borderTop: '1px solid #B6BCCE',
    paddingTop: '7px',
    marginTop: '13px',
  },
}));

export const UserDetails: React.FC = () => {
  const classes = useStyles();

  const dispatch = useAppDispatch();
  const userDetailsRef = useRef<HTMLDivElement>(null);

  const [openUserDetails, setOpenUserDetails] = useState<boolean>(false);

  const { userMainAccount } = useAppSelector(selectedAccStateSelector);

  const userId: number = useAppSelector(selectUserProfileId);
  const { name: userName }: { name: string } = useAppSelector(selectOidcUserLogged) ?? {
    name: '',
  };

  const { userAccessProfile, getUserAccessProfile, loading, error } = useUserDetailsDataSource();

  useEffect(() => {
    if (userId) {
      getUserAccessProfile(userId);
    }
  }, [userId, getUserAccessProfile]);

  const userRole = useMemo(
    () =>
      userAccessProfile
        ? userAccessProfile?.roles?.find((role) => role.applicationName === environment.appName)?.roleName || null
        : '',
    [userAccessProfile]
  );

  useEffect(() => {
    if (userAccessProfile && !loading && userId && !error) {
      dispatch(accountsReducer.slice.actions.setUserRole(userRole));
    }
  }, [dispatch, userAccessProfile, userId, userRole, loading, error]);

  return (
    <div ref={userDetailsRef}>
      <Button
        variant="outlined"
        onClick={() => setOpenUserDetails(!openUserDetails)}
        style={{ fontSize: '16px' }}
        endIcon={openUserDetails ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        startIcon={<PersonIcon />}
        dataTestId="layout-navbar-buttons-user-button"
      >
        {userName}
      </Button>
      <Menu
        open={openUserDetails}
        anchorEl={userDetailsRef?.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClick={() => setOpenUserDetails(false)}
        classes={{
          paper: classes.userDetailsMenuWrapper,
          list: classes.userDetailsMenuListWrapper,
        }}
        data-testid="layout-navbar-buttons-user-menu"
      >
        <div data-testid="layout-navbar-buttons-parent-account">
          <Trans>Parent Account: {userMainAccount?.name}</Trans>
        </div>
        <div data-testid="layout-navbar-buttons-user-role">
          <Trans>User Level: {loading ? '' : userRole}</Trans>
        </div>

        <Button
          variant="outlined"
          onClick={() =>
            logout({
              sessionCookieDomain: environment.sessionCookieDomain,
              sessionCookieIsSecure: environment.sessionCookieSecure,
              sessionIdleTimeout: environment.sessionCookieIdleTimeout,
            })
          }
          style={{ fontSize: '16px' }}
          dataTestId="layout-navbar-buttons-user-logout-button"
        >
          <Trans>Log out</Trans>
        </Button>
        <div className={classes.version} data-testid="layout-navbar-buttons-user-version">
          <Trans comment="Application version">Version</Trans>: {environment.version}
        </div>
      </Menu>
    </div>
  );
};
